import { Col, Collapse, Form, Input, Row, Select } from 'antd';

import { stateOptions } from '@/config';

const { Option } = Select;

interface ReimbursementAddressProps {
  meData: any;
  form: any;
}

const ReimbursementAddress = (props: ReimbursementAddressProps) => {
  const { meData, form: parentForm } = props;

  // Define address fields that need validation
  const addressFields = [
    'dispensing_pharmacy_addr',
    'dispensing_pharmacy_addr_line2',
    'dispensing_pharmacy_addr_city',
    'dispensing_pharmacy_addr_state',
    'dispensing_pharmacy_addr_zip',
  ];

  // Rule for validating fields when any address field has a value
  const getAddressFieldRule = () => ({
    validator: async (_: any, value: any) => {
      // Only run validation if the parent form is available
      if (!parentForm) return Promise.resolve();

      // Get all form values
      const allValues = parentForm.getFieldsValue();

      // Check if any address field has a value
      const hasAnyValue = addressFields.some(
        (field) =>
          allValues[field] !== undefined && allValues[field] !== null && allValues[field] !== ''
      );

      console.log('hasAnyValue: ', hasAnyValue);

      // If any field has a value and current field is empty, throw error
      if (hasAnyValue && (!value || value === '')) {
        throw new Error('This field is required when any address field is filled');
      }

      return Promise.resolve();
    },
  });

  return (
    <Collapse
      className="mt-6"
      items={[
        {
          key: '1',
          label:
            'Expand this section if you would like to specify an alternative reimbursement address for this assistance request.',
          children: (
            <Row gutter={20}>
              <Col span={24}>
                <Form.Item>
                  <span>
                    The fields below are required only if you would like to re-direct copay
                    assistance reimbursement to a different address than that listed above. If the
                    address fields below are left blank, reimbursement will be sent to the{' '}
                    {meData.business_name} address listed above.
                  </span>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="dispensing_pharmacy_business_name"
                  label="Reimbursement business name"
                >
                  <Input placeholder="Name of the business the reimbursement must be directed to and address below is for" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="dispensing_pharmacy_addr"
                  label="Reimbursement address"
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  rules={[getAddressFieldRule()]}
                >
                  <Input placeholder="Street address where financial reimbursement would be sent for this assistance request" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  name="dispensing_pharmacy_addr_line2"
                  label="Reimbursement address line 2"
                  rules={[getAddressFieldRule()]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  name="dispensing_pharmacy_addr_city"
                  label="Financial reimbursement city"
                  rules={[getAddressFieldRule()]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="dispensing_pharmacy_addr_state"
                  label="Financial reimbursement state"
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  rules={[getAddressFieldRule()]}
                >
                  <Select allowClear placeholder="Please select state">
                    {stateOptions.map((option) => (
                      <Option value={option.value} key={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="dispensing_pharmacy_addr_zip"
                  label="Financial reimbursement ZIP code"
                  validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                  rules={[getAddressFieldRule()]}
                >
                  <Input.OTP length={5} />
                </Form.Item>
              </Col>

              <Col span={16}>
                <Form.Item
                  name="dispensing_pharmacy_contact_name"
                  label="Reimbursement contact name"
                >
                  <Input placeholder="First and last name of the financial reimbursement contact at this address" />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="dispensing_pharmacy_contact_phone"
                  label="Reimbursement contact phone"
                >
                  <Input.OTP length={10} />
                </Form.Item>
              </Col>
            </Row>
          ),
        },
      ]}
    />
  );
};

export default ReimbursementAddress;
